/**
 * frontDeskReader.ts
 *
 * Per-computer state for the front desk tap pads.
 *
 * A patient holds their phone to the pad at a desk; the phone app tells the
 * server the patient has arrived; the browser sitting on that desk picks the
 * arrival up and opens the chart. Which computer is "the browser sitting on
 * that desk" is not something the server can know, so it is stored here, in
 * this browser, alongside the two bits of bookkeeping that keep one arrival
 * from being opened twice:
 *
 *   - a per-tab id, so the server can record which tab won a claim;
 *   - a per-desk "already seen" set, so a repeated poll response and a claim
 *     already won by a sibling tab are both skipped without a request.
 *
 * Deliberately free of React, zustand and service imports. Every rule that is
 * cheap to get wrong (account scoping, the poll cursor, pruning, degrading
 * when storage is refused) lives here so it can be tested with no DOM at all.
 *
 * Storage notes:
 *   - The config is scoped to an accountID. A corporate account switch must
 *     read as "off" rather than keep pointing this PC at the other clinic's
 *     desk, which would open charts belonging to a different account.
 *   - The seen-set key carries the deskID, so moving this computer to another
 *     desk starts from a clean cursor instead of inheriting the old desk's
 *     highest arrival id and skipping real arrivals.
 *   - Every read and write is wrapped: a private window throws on the first
 *     property access, and this module is reached from the app shell, so an
 *     uncaught throw here takes the whole page down.
 */

export interface FrontDeskConfig {
  /** Off by default; the operator opts this computer in from Settings. */
  enabled: boolean;
  /** The account this was set up for. Anything else reads as off. */
  accountID: number;
  /**
   * The staff member who turned it on. Anything else reads as off.
   *
   * A front desk computer is shared, and localStorage is not: without this,
   * the next person to sign in on that browser inherits arrival toasts they
   * never asked for and cannot explain. Turning it on is a choice, so it is
   * stored against whoever made it.
   */
  userID: number;
  deskID: number;
  deskName: string;
}

/** Where the per-computer desk choice lives. */
export const STORAGE_KEY = 'ftp-front-desk-reader';

/** Per-tab id, kept in sessionStorage so each tab gets its own. */
const TAB_KEY = 'ftp-front-desk-tab';

/** Per-desk "already handled" set. The deskID is appended. */
const SEEN_KEY_PREFIX = 'ftp-front-desk-seen-';

/**
 * Arrivals expire server-side after 10 minutes, so an hour of memory is well
 * past the point where an id could come back. Matches the prune window in
 * useNotificationPopups.
 */
const SEEN_PRUNE_MS = 1000 * 60 * 60;

/** Cross-tab channel name. Both the wrapper below and nothing else use it. */
const CHANNEL_NAME = 'ftp-front-desk';

/**
 * Custom event this app dispatches on `window` after saving the config.
 * The `storage` event does not fire in the tab that wrote the value, so the
 * Settings tab announces its own save with this instead.
 */
export const CONFIG_EVENT = 'ftp-front-desk-config';

interface SeenEntry {
  id: number;
  t: number;
}

/** localStorage, or null wherever it is absent or refused. */
function local(): Storage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}

/** sessionStorage, or null wherever it is absent or refused. */
function session(): Storage | null {
  try {
    return typeof sessionStorage === 'undefined' ? null : sessionStorage;
  } catch {
    return null;
  }
}

function isConfig(value: unknown): value is FrontDeskConfig {
  if (!value || typeof value !== 'object') return false;
  const c = value as Partial<FrontDeskConfig>;
  return (
    typeof c.enabled === 'boolean' &&
    typeof c.accountID === 'number' &&
    typeof c.userID === 'number' &&
    typeof c.deskID === 'number'
  );
}

/**
 * The desk this computer is set up for, or null when it is not set up at all.
 *
 * Null covers every "off" case on purpose — absent, unparseable, toggled off,
 * saved for a different account, or saved by a different staff member — so
 * callers have one condition to check and cannot accidentally act on a desk
 * that was set up for someone else.
 *
 * A blob written before `userID` existed fails `isConfig` and reads as off,
 * which is the right default: it was set up by someone we can no longer
 * identify, so the next person re-opts in rather than inheriting it.
 */
export function loadConfig(accountId: number, userId: number): FrontDeskConfig | null {
  const store = local();
  if (!store) return null;
  try {
    const raw = store.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (!isConfig(parsed)) return null;
    if (!parsed.enabled) return null;
    if (parsed.accountID !== accountId) return null;
    if (parsed.userID !== userId) return null;
    if (parsed.deskID <= 0) return null;
    return {
      enabled: true,
      accountID: parsed.accountID,
      userID: parsed.userID,
      deskID: parsed.deskID,
      deskName: typeof parsed.deskName === 'string' ? parsed.deskName : '',
    };
  } catch {
    return null;
  }
}

export function saveConfig(cfg: FrontDeskConfig): void {
  const store = local();
  if (!store) return;
  try {
    store.setItem(STORAGE_KEY, JSON.stringify(cfg));
  } catch {
    // Storage full or refused — the toggle simply does not persist.
  }
}

export function clearConfig(): void {
  const store = local();
  if (!store) return;
  try {
    store.removeItem(STORAGE_KEY);
  } catch {
    // Nothing to do; the caller has already stopped using the value.
  }
}

/** Memoised so the id is stable even where sessionStorage is refused. */
let cachedTabId = '';

function randomId(): string {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
  } catch {
    // Fall through to the arithmetic fallback below.
  }
  return `t-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * A stable id for this browser tab, generated once.
 *
 * Two tabs on the same desk PC both poll and both may attempt the same
 * arrival; the server records the winner's tab id, and this is that value.
 */
export function tabId(): string {
  if (cachedTabId) return cachedTabId;
  const store = session();
  if (store) {
    try {
      const existing = store.getItem(TAB_KEY);
      if (existing) {
        cachedTabId = existing;
        return cachedTabId;
      }
    } catch {
      // Unreadable — fall through and mint one in memory.
    }
  }
  cachedTabId = randomId();
  if (store) {
    try {
      store.setItem(TAB_KEY, cachedTabId);
    } catch {
      // In-memory only for this tab, which is all the id is used for.
    }
  }
  return cachedTabId;
}

/**
 * Arrival ids this computer has already handled for `deskID`, mapped to when
 * it handled them. Entries older than an hour are dropped on read so the blob
 * cannot grow without bound on a desk that runs for weeks.
 */
export function loadSeen(deskID: number): Map<number, number> {
  const store = local();
  if (!store) return new Map();
  try {
    const raw = store.getItem(SEEN_KEY_PREFIX + deskID);
    if (!raw) return new Map();
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return new Map();
    const cutoff = Date.now() - SEEN_PRUNE_MS;
    const entries = (parsed as SeenEntry[]).filter(
      (e) => e && typeof e.id === 'number' && typeof e.t === 'number' && e.t >= cutoff,
    );
    return new Map(entries.map((e) => [e.id, e.t]));
  } catch {
    return new Map();
  }
}

export function saveSeen(deskID: number, seen: Map<number, number>): void {
  const store = local();
  if (!store) return;
  try {
    const entries: SeenEntry[] = Array.from(seen.entries()).map(([id, t]) => ({ id, t }));
    store.setItem(SEEN_KEY_PREFIX + deskID, JSON.stringify(entries));
  } catch {
    // Storage full or refused — the set still holds for this page's lifetime.
  }
}

/**
 * The poll cursor: the highest arrival id already handled, so the server can
 * skip everything at or below it. 0 asks for everything still open.
 */
export function cursorFrom(seen: Map<number, number>): number {
  let max = 0;
  for (const id of seen.keys()) {
    if (id > max) max = id;
  }
  return max;
}

export interface FrontDeskChannel {
  /** Tell sibling tabs this arrival is taken. */
  postClaimed(arrivalID: number): void;
  /** Listen for arrivals sibling tabs took. Returns an unsubscribe. */
  onClaimed(cb: (arrivalID: number) => void): () => void;
  close(): void;
}

interface ClaimedMessage {
  type: 'claimed';
  arrivalID: number;
}

/**
 * A cross-tab channel for "I took this one".
 *
 * This is an optimisation, never a guarantee: exactly-once is enforced by the
 * server's conditional update. Where BroadcastChannel is missing the returned
 * object is a set of no-ops and every tab simply attempts its own claim, which
 * is the same outcome with one extra losing request.
 */
export function openChannel(): FrontDeskChannel {
  let channel: BroadcastChannel | null = null;
  try {
    if (typeof BroadcastChannel !== 'undefined') {
      channel = new BroadcastChannel(CHANNEL_NAME);
    }
  } catch {
    channel = null;
  }

  return {
    postClaimed(arrivalID: number): void {
      if (!channel) return;
      try {
        const message: ClaimedMessage = { type: 'claimed', arrivalID };
        channel.postMessage(message);
      } catch {
        // A closed or unavailable channel costs nothing here.
      }
    },
    onClaimed(cb: (arrivalID: number) => void): () => void {
      if (!channel) return () => undefined;
      const target = channel;
      const handler = (event: MessageEvent) => {
        const data = event.data as Partial<ClaimedMessage> | null;
        if (!data || data.type !== 'claimed' || typeof data.arrivalID !== 'number') return;
        cb(data.arrivalID);
      };
      try {
        target.addEventListener('message', handler);
      } catch {
        return () => undefined;
      }
      return () => {
        try {
          target.removeEventListener('message', handler);
        } catch {
          // Already torn down.
        }
      };
    },
    close(): void {
      if (!channel) return;
      try {
        channel.close();
      } catch {
        // Already closed.
      }
      channel = null;
    },
  };
}
